/**
 * OnboardingWizard — S03 first-run setup (Phase J of the design-takeover).
 *
 * Route: rendered as a modal over the main shell on first sign-in.
 * Trigger: caller checks `me.pairs.length === 0` (no active pairs yet).
 *
 * Three steps:
 *   1. Role pick  — report | manager | both
 *   2. Cadence    — weekly | fortnightly | monthly
 *   3. Core cards intro — informational only
 *
 * After "Get started" (step 3):
 *   - Saves onboarding state (role + cadence) via onComplete(role, cadence).
 *   - If role is 'manager' or 'both', caller routes to /pair/invite AFTER
 *     the wizard closes — the wizard itself does not navigate.
 *
 * A11y:
 *   - Step transition respect prefers-reduced-motion.
 *   - Focus trapped in the modal; Escape dismisses only on step 1 (no
 *     progress lost) — on steps 2–3 Escape is suppressed to prevent accidental
 *     dismissal mid-setup.
 *   - Step indicator announces current step via aria-live.
 *   - Role and cadence options use radio semantics (role="radio",
 *     aria-checked) grouped in a radiogroup.
 */
import React, { useEffect, useRef, useState } from 'react'
import { Button, useFocusTrap } from '../ui'

// ─── Copy (from copy-decisions doc Phase J / S03) ──────────────────────────

const ROLE_OPTIONS = [
  {
    value: 'report',
    label: "I'm a report",
    description: "My manager is here too. I'll prepare for our 1:1s and share my prep with them.",
  },
  {
    value: 'manager',
    label: "I'm a manager",
    description:
      "I have direct reports in Soundcheck. I'll review their prep and collaborate in sessions.",
  },
  {
    value: 'both',
    label: "I'm both",
    description:
      "I manage a team and I also report to someone. I'll prep for my own 1:1s and review my reports'.",
  },
]

const CADENCE_OPTIONS = [
  { value: 'weekly', label: 'Weekly', description: 'Once a week.' },
  { value: 'fortnightly', label: 'Fortnightly', description: 'Once every two weeks.' },
  { value: 'monthly', label: 'Monthly', description: 'Once a month.' },
]

// ─── Helpers ───────────────────────────────────────────────────────────────

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false,
  )
  useEffect(() => {
    const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)')
    if (!mq) return
    const handler = (e) => setReduced(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return reduced
}

// ─── OptionCard ─────────────────────────────────────────────────────────────

function OptionCard({ value, label, description, selected, onSelect, groupName }) {
  const id = `wizard-option-${groupName}-${value}`
  return (
    <div
      className={`wizard__option${selected ? ' wizard__option--selected' : ''}`}
      role="radio"
      aria-checked={selected}
      aria-labelledby={`${id}-label`}
      aria-describedby={`${id}-desc`}
      tabIndex={0}
      onClick={() => onSelect(value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect(value)
        }
      }}
    >
      <span className="wizard__option-radio" aria-hidden="true">
        <span className="wizard__option-radio-inner" />
      </span>
      <span className="wizard__option-text">
        <span id={`${id}-label`} className="wizard__option-label">
          {label}
        </span>
        <span id={`${id}-desc`} className="wizard__option-desc">
          {description}
        </span>
      </span>
    </div>
  )
}

// ─── Step components ────────────────────────────────────────────────────────

function StepRole({ role, onSelect }) {
  return (
    <div className="wizard__step">
      <h2 className="wizard__step-heading">How will you use Soundcheck?</h2>
      <p className="wizard__step-helper">
        You can change this later. Choose the option that fits your current relationship.
      </p>
      <div
        role="radiogroup"
        aria-label="Your role"
        className="wizard__options"
      >
        {ROLE_OPTIONS.map((opt) => (
          <OptionCard
            key={opt.value}
            {...opt}
            selected={role === opt.value}
            onSelect={onSelect}
            groupName="role"
          />
        ))}
      </div>
    </div>
  )
}

function StepCadence({ cadence, onSelect }) {
  return (
    <div className="wizard__step">
      <h2 className="wizard__step-heading">How often do you meet?</h2>
      <p className="wizard__step-helper">
        This sets the default session frequency for your 1:1s. You can adjust it per pair later.
      </p>
      <div
        role="radiogroup"
        aria-label="Meeting cadence"
        className="wizard__options"
      >
        {CADENCE_OPTIONS.map((opt) => (
          <OptionCard
            key={opt.value}
            {...opt}
            selected={cadence === opt.value}
            onSelect={onSelect}
            groupName="cadence"
          />
        ))}
      </div>
    </div>
  )
}

function StepCoreCards() {
  return (
    <div className="wizard__step">
      <h2 className="wizard__step-heading">Your prep cards</h2>
      <p className="wizard__step-body">
        Every session starts with a set of cards you fill in before the meeting. Your manager sees
        your answers and can mark items for discussion. By default you get a core set — Wins,
        Priorities, Asks, Risks, and a few others. You can add or remove cards at any time from
        your session canvas.
      </p>
      <p className="wizard__step-helper">
        You don&apos;t need to fill every card. Use the ones that are useful.
      </p>
    </div>
  )
}

// ─── OnboardingWizard ───────────────────────────────────────────────────────

const TOTAL_STEPS = 3

/**
 * @param {object} props
 * @param {function} props.onComplete - Called with (role, cadence) when the
 *   wizard finishes. Caller is responsible for saving and routing.
 * @param {function} [props.onDismiss] - Called if the wizard is dismissed
 *   early (only possible from step 1).
 */
export function OnboardingWizard({ onComplete, onDismiss }) {
  const [step, setStep] = useState(1)
  const [role, setRole] = useState('report')
  const [cadence, setCadence] = useState('weekly')
  const [saving, setSaving] = useState(false)
  const reducedMotion = useReducedMotion()
  const dialogRef = useRef(null)

  useFocusTrap(dialogRef, true)

  // Announce step changes to screen readers
  const stepLabel = `Step ${step} of ${TOTAL_STEPS}`

  function handleNext() {
    if (step < TOTAL_STEPS) setStep((s) => s + 1)
  }

  function handleBack() {
    if (step > 1) setStep((s) => s - 1)
  }

  async function handleFinish() {
    setSaving(true)
    try {
      await onComplete(role, cadence)
    } finally {
      setSaving(false)
    }
  }

  function handleKeyDown(e) {
    // Escape: only dismiss from step 1 (no progress lost)
    if (e.key === 'Escape' && step === 1) {
      onDismiss?.()
    }
  }

  const stepContent = {
    1: <StepRole role={role} onSelect={setRole} />,
    2: <StepCadence cadence={cadence} onSelect={setCadence} />,
    3: <StepCoreCards />,
  }[step]

  return (
    <div
      className="wizard__backdrop"
      role="presentation"
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        className="wizard__dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Set up Soundcheck"
        data-reduced-motion={reducedMotion || undefined}
      >
        {/* Header */}
        <div className="wizard__header">
          <h1 className="wizard__title">Set up Soundcheck</h1>
          <p
            className="wizard__step-indicator"
            aria-live="polite"
            aria-atomic="true"
          >
            {stepLabel}
          </p>
        </div>

        {/* Progress bar */}
        <div
          className="wizard__progress"
          role="progressbar"
          aria-valuemin={1}
          aria-valuemax={TOTAL_STEPS}
          aria-valuenow={step}
          aria-label={stepLabel}
        >
          <div
            className="wizard__progress-fill"
            style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
          />
        </div>

        {/* Step content */}
        <div
          className="wizard__body"
          data-step={step}
        >
          {stepContent}
        </div>

        {/* Navigation */}
        <div className="wizard__footer">
          {step > 1 && (
            <Button variant="ghost" onClick={handleBack} disabled={saving}>
              Back
            </Button>
          )}
          <div className="wizard__footer-right">
            {step < TOTAL_STEPS ? (
              <Button variant="primary" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={handleFinish}
                loading={saving}
                loadingLabel="Setting up…"
              >
                Get started
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
